import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Search, TrendingUp, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/utils/api';
import { toTitleCase } from '@/utils/format';

const fmtINR = (val) => Math.round(val || 0).toLocaleString('en-IN');
const monthShort = (m) => {
  if (!m) return '';
  try { const d = new Date(m + '-01'); return d.toLocaleString('en-IN', { month: 'short', year: '2-digit' }); } catch { return m; }
};
const monthLabel = (m) => {
  if (!m) return '';
  try { const d = new Date(m + '-01'); return d.toLocaleString('en-IN', { month: 'short' }) + ' ' + m.split('-')[0]; } catch { return m; }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.name}:</span>
          <span className="font-mono font-semibold">{typeof p.value === 'number' && p.value > 100 ? fmtINR(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

const getDefaultMonths = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  return {
    from: `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}`,
    to: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  };
};

const Reports = ({ user }) => {
  const defaults = getDefaultMonths();
  const [reportType, setReportType] = useState('salary');
  const [costCentres, setCostCentres] = useState([]);
  const [selectedCC, setSelectedCC] = useState('');
  const [searchName, setSearchName] = useState('');
  const [fromMonth, setFromMonth] = useState(defaults.from);
  const [toMonth, setToMonth] = useState(defaults.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get('/cost-centres').then(res => setCostCentres(res.data)).catch(() => {}); }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromMonth) params.from_month = fromMonth;
      if (toMonth) params.to_month = toMonth;
      if (selectedCC) params.cost_centre_id = selectedCC;
      if (searchName.trim()) params.employee_name = searchName.trim();
      const res = await api.get(`/reports/${reportType}`, { params });
      setData(res.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [reportType, fromMonth, toMonth, selectedCC, searchName]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.post('/reports/export', {
        report_type: reportType,
        from_month: fromMonth || undefined,
        to_month: toMonth || undefined,
        cost_centre_id: selectedCC || undefined,
        employee_name: searchName.trim() || undefined,
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType.toUpperCase()}_Report_${fromMonth || 'all'}_${toMonth || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Report exported');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const months = data?.months || [];
  const employees = data?.employees || [];
  const records = data?.records || [];

  const statusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-slate-100 text-slate-700 border-slate-300';
      case 'Submitted': return 'bg-blue-50 text-blue-700 border-blue-300';
      case 'Manager Approved': return 'bg-amber-50 text-amber-700 border-amber-300';
      case 'CEO Approved': return 'bg-emerald-50 text-emerald-700 border-emerald-300';
      case 'Banking Done - Freezed': return 'bg-purple-50 text-purple-700 border-purple-300';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Chart data
  const chartData = months.map(m => {
    if (reportType === 'salary') {
      let total = 0, count = 0;
      employees.forEach(e => { if (e.months[m]) { total += e.months[m]; count++; } });
      return { month: monthShort(m), 'Net Salary': total, Employees: count };
    } else {
      let otAmt = 0, odwAmt = 0, count = 0;
      employees.forEach(e => {
        const md = e.months[m];
        if (md) { otAmt += md.ot_amount; odwAmt += md.odw_amount; count++; }
      });
      return { month: monthShort(m), 'OT Amount': otAmt, 'ODW Amount': odwAmt, Employees: count };
    }
  });

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Reports</h1>
          <p className="mt-1 text-base text-slate-600">Analyze salary and overtime data</p>
        </div>
        <Button onClick={handleExport} disabled={exporting || !employees.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-report-button">
          <Download className="h-4 w-4 mr-1.5" />
          {exporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div className="min-w-[140px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Report For</label>
          <select value={reportType} onChange={e => setReportType(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="report-type-select">
            <option value="salary">Salary</option>
            <option value="ot">OT (Overtime)</option>
          </select>
        </div>
        <div className="min-w-[130px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">From Month</label>
          <Input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} className="w-40" data-testid="report-from-month" />
        </div>
        <div className="min-w-[130px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">To Month</label>
          <Input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)} className="w-40" data-testid="report-to-month" />
        </div>
        <div className="min-w-[160px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Cost Centre</label>
          <select value={selectedCC} onChange={e => setSelectedCC(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="report-cc-select">
            <option value="">All</option>
            {costCentres.map(cc => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Search by Employee</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={searchName} onChange={e => setSearchName(e.target.value)} placeholder="Type employee name..." className="pl-9" data-testid="report-search-name" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div></div>
      ) : !employees.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <TrendingUp className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">No Data Available</h3>
          <p className="text-slate-600 mt-2">No approved salary records found for the selected filters.</p>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="report-chart">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              {reportType === 'salary' ? 'Monthly Salary Overview' : 'Monthly OT & ODW Overview'}
            </h2>
            <ResponsiveContainer width="100%" height={280}>
              {reportType === 'salary' ? (
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="Net Salary" fill="#6366f1" radius={[4,4,0,0]} />
                  <Bar yAxisId="right" dataKey="Employees" fill="#10b981" radius={[4,4,0,0]} />
                </BarChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="OT Amount" fill="#6366f1" radius={[4,4,0,0]} />
                  <Bar yAxisId="left" dataKey="ODW Amount" fill="#f59e0b" radius={[4,4,0,0]} />
                  <Bar yAxisId="right" dataKey="Employees" fill="#10b981" radius={[4,4,0,0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Data Table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="report-table">
            <div className="overflow-x-auto">
              {reportType === 'salary' ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-3 py-2.5 text-center font-semibold min-w-[40px]">Sr</th>
                      <th className="px-3 py-2.5 text-left font-semibold min-w-[160px]">Employee Name</th>
                      <th className="px-3 py-2.5 text-left font-semibold min-w-[100px]">Cost Centre</th>
                      <th className="px-3 py-2.5 text-left font-semibold min-w-[90px]">Month</th>
                      <th className="px-3 py-2.5 text-right font-semibold min-w-[100px]">Net Salary</th>
                      <th className="px-3 py-2.5 text-center font-semibold min-w-[160px]" data-testid="status-column-header">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {records.map((rec, idx) => (
                      <tr key={`${rec.employee_id}-${rec.month}`} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-center text-slate-500 font-mono">{idx + 1}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">{toTitleCase(rec.name)}</td>
                        <td className="px-3 py-2 text-slate-600">{rec.cost_centre}</td>
                        <td className="px-3 py-2 text-slate-700">{monthLabel(rec.month)}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-700">{fmtINR(rec.net_salary)}</td>
                        <td className="px-3 py-2 text-center" data-testid={`status-${idx}`}>
                          <span className={`inline-block px-2.5 py-0.5 text-[10px] font-semibold rounded-full border ${statusColor(rec.status)}`}>
                            {rec.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                      <td className="px-3 py-2.5" colSpan={4}>TOTAL ({records.length} records)</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-sm">{fmtINR(records.reduce((s, r) => s + r.net_salary, 0))}</td>
                      <td className="px-3 py-2.5"></td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-2 py-2.5 text-center font-semibold sticky left-0 bg-slate-800 z-10 min-w-[36px]" rowSpan={2}>Sr</th>
                      <th className="px-3 py-2.5 text-left font-semibold sticky left-[36px] bg-slate-800 z-10 min-w-[150px]" rowSpan={2}>Employee Name</th>
                      <th className="px-3 py-2.5 text-left font-semibold min-w-[90px]" rowSpan={2}>Cost Centre</th>
                      {months.map(m => (
                        <th key={m} className="px-1 py-1.5 text-center font-semibold border-b border-slate-600" colSpan={5}>{monthLabel(m)}</th>
                      ))}
                      <th className="px-3 py-2.5 text-right font-semibold min-w-[90px] bg-slate-900" rowSpan={2}>Grand Total</th>
                    </tr>
                    <tr className="bg-slate-700 text-white text-[10px]">
                      {months.map(m => (
                        <React.Fragment key={m}>
                          <th className="px-1.5 py-1.5 text-right font-medium min-w-[60px]">OT Hrs</th>
                          <th className="px-1.5 py-1.5 text-right font-medium min-w-[70px] text-amber-300">OT Amt</th>
                          <th className="px-1.5 py-1.5 text-right font-medium min-w-[60px]">ODW Days</th>
                          <th className="px-1.5 py-1.5 text-right font-medium min-w-[70px] text-orange-300">ODW Amt</th>
                          <th className="px-1.5 py-1.5 text-right font-medium min-w-[70px] text-emerald-300">Total</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {employees.map((emp, idx) => (
                      <tr key={emp.employee_id} className="hover:bg-slate-50">
                        <td className="px-2 py-2 text-center text-slate-500 font-mono sticky left-0 bg-white z-10">{idx + 1}</td>
                        <td className="px-3 py-2 font-medium text-slate-900 sticky left-[36px] bg-white z-10">{toTitleCase(emp.name)}</td>
                        <td className="px-3 py-2 text-slate-600">{emp.cost_centre}</td>
                        {months.map(m => {
                          const md = emp.months[m];
                          if (!md) return (
                            <React.Fragment key={m}>
                              <td className="px-1.5 py-2 text-right text-slate-300">—</td>
                              <td className="px-1.5 py-2 text-right text-slate-300">—</td>
                              <td className="px-1.5 py-2 text-right text-slate-300">—</td>
                              <td className="px-1.5 py-2 text-right text-slate-300">—</td>
                              <td className="px-1.5 py-2 text-right text-slate-300">—</td>
                            </React.Fragment>
                          );
                          return (
                            <React.Fragment key={m}>
                              <td className="px-1.5 py-2 text-right font-mono text-slate-700">{md.ot_hours || '—'}</td>
                              <td className="px-1.5 py-2 text-right font-mono text-amber-600 font-semibold">{md.ot_amount ? fmtINR(md.ot_amount) : '—'}</td>
                              <td className="px-1.5 py-2 text-right font-mono text-slate-700">{md.odw_hours || '—'}</td>
                              <td className="px-1.5 py-2 text-right font-mono text-orange-600 font-semibold">{md.odw_amount ? fmtINR(md.odw_amount) : '—'}</td>
                              <td className="px-1.5 py-2 text-right font-mono font-bold text-slate-900">{md.total ? fmtINR(md.total) : '—'}</td>
                            </React.Fragment>
                          );
                        })}
                        <td className="px-3 py-2 text-right font-mono font-bold text-slate-900 bg-slate-50">{emp.grand_total ? fmtINR(emp.grand_total) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                      <td className="px-2 py-2.5 sticky left-0 bg-slate-100 z-10"></td>
                      <td className="px-3 py-2.5 sticky left-[36px] bg-slate-100 z-10">TOTAL ({employees.length})</td>
                      <td className="px-3 py-2.5"></td>
                      {months.map(m => {
                        let otH = 0, otA = 0, odwH = 0, odwA = 0, tot = 0;
                        employees.forEach(e => {
                          const md = e.months[m];
                          if (md) { otH += md.ot_hours; otA += md.ot_amount; odwH += md.odw_hours; odwA += md.odw_amount; tot += md.total; }
                        });
                        return (
                          <React.Fragment key={m}>
                            <td className="px-1.5 py-2.5 text-right font-mono">{otH || '—'}</td>
                            <td className="px-1.5 py-2.5 text-right font-mono text-amber-600">{otA ? fmtINR(otA) : '—'}</td>
                            <td className="px-1.5 py-2.5 text-right font-mono">{odwH || '—'}</td>
                            <td className="px-1.5 py-2.5 text-right font-mono text-orange-600">{odwA ? fmtINR(odwA) : '—'}</td>
                            <td className="px-1.5 py-2.5 text-right font-mono font-bold">{tot ? fmtINR(tot) : '—'}</td>
                          </React.Fragment>
                        );
                      })}
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-sm bg-slate-200">{fmtINR(employees.reduce((s, e) => s + (e.grand_total || 0), 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
